import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type OnChangeFn,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode } from 'react';

import { cn } from '../../lib/utils';

type ColumnVisibilityState = Record<string, boolean>;

interface Column<T> {
  key: string;
  header: ReactNode;
  accessor?: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  enableSorting?: boolean;
  enableHiding?: boolean;
  sortingFn?: (rowA: T, rowB: T, columnId: string) => number;
  sortDescFirst?: boolean;
  size?: number;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
  tableClassName?: string;
  theadClassName?: string;
  headerRowClassName?: string;
  tbodyClassName?: string;
  rowBaseClassName?: string;
  rowClassName?: string | ((row: T, index: number) => string);
  onRowClick?: (row: T) => void;
  selectedRowId?: string;
  getRowId?: (row: T) => string;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  columnVisibility?: ColumnVisibilityState;
  onColumnVisibilityChange?: OnChangeFn<ColumnVisibilityState>;
  enableSorting?: boolean;
  renderMobileItem?: (row: T) => ReactNode;
  skeletonRowCount?: number;
}

export function DataTable<T>({
  data,
  columns,
  isLoading = false,
  emptyMessage = 'No data found',
  className = '',
  tableClassName,
  theadClassName,
  headerRowClassName,
  tbodyClassName,
  rowBaseClassName,
  rowClassName,
  onRowClick,
  selectedRowId,
  getRowId,
  sorting,
  onSortingChange,
  columnVisibility,
  onColumnVisibilityChange,
  enableSorting = false,
  renderMobileItem,
  skeletonRowCount = 8,
}: DataTableProps<T>) {
  'use no memo';

  type ColumnMeta = {
    cellClassName?: string;
    headerClassName?: string;
  };

  const columnDefs: ColumnDef<T, unknown>[] = columns.map((column) => {
    const colDef: ColumnDef<T, unknown> = {
      id: column.key,
      header: () => column.header,
      accessorFn: (row) => row,
      cell: (context) =>
        column.accessor
          ? column.accessor(context.row.original)
          : (context.row.original as Record<string, ReactNode | undefined>)[
              column.key
            ],
      enableSorting: column.enableSorting ?? enableSorting,
      enableHiding: column.enableHiding,
      sortDescFirst: column.sortDescFirst,
      size: column.size,
      meta: {
        cellClassName: column.className,
        headerClassName: column.headerClassName ?? column.className,
      } satisfies ColumnMeta,
    };

    if (column.sortingFn) {
      colDef.sortingFn = (rowA, rowB, columnId) =>
        column.sortingFn?.(rowA.original, rowB.original, columnId) ?? 0;
    }

    return colDef;
  });

  const table = useReactTable({
    data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSorting: enableSorting,
    state: {
      sorting: sorting || [],
      columnVisibility,
    },
    onSortingChange: onSortingChange,
    onColumnVisibilityChange,
    getRowId: getRowId ? (originalRow: T) => getRowId(originalRow) : undefined,
  });

  const getRowClass = (row: T, index: number) => {
    const baseClass = rowBaseClassName
      ? rowBaseClassName
      : 'border-b border-neutral-800 hover:bg-neutral-800/60 transition-colors';
    const customClass =
      typeof rowClassName === 'function'
        ? rowClassName(row, index)
        : (rowClassName ?? '');
    const clickableClass = onRowClick ? 'cursor-pointer' : '';
    const isSelected =
      selectedRowId && getRowId ? getRowId(row) === selectedRowId : false;
    const selectedClass = isSelected
      ? 'bg-teal-900/20 border-l-2 border-l-teal-400'
      : '';
    return cn(baseClass, clickableClass, customClass, selectedClass);
  };

  const isInitialLoading = isLoading && data.length === 0;

  if (data.length === 0 && !isLoading) {
    return (
      <div className="text-center py-8 text-neutral-400">{emptyMessage}</div>
    );
  }

  const visibleColumns = table
    .getVisibleLeafColumns()
    .filter((column) => column.id !== undefined);

  return (
    <div className={cn('relative', className)}>
      {isLoading && data.length > 0 && (
        <div className="absolute inset-0 bg-neutral-900/50 z-10 pointer-events-none" />
      )}

      {renderMobileItem && (
        <div className="block md:hidden space-y-3">
          {isInitialLoading
            ? Array.from({ length: skeletonRowCount }).map((_, index) => (
                <div
                  key={`mobile-skeleton-${index}`}
                  className="bg-neutral-800/30 rounded-lg p-4 border border-neutral-700/50"
                  aria-hidden="true"
                >
                  <div className="space-y-3 animate-pulse">
                    <div className="flex items-start gap-3">
                      <div className="size-10 rounded-lg bg-neutral-700 flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-neutral-700 rounded w-2/3" />
                        <div className="h-3 bg-neutral-700 rounded w-1/3" />
                      </div>
                      <div className="h-5 bg-neutral-700 rounded-full w-16" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-3 bg-neutral-700 rounded" />
                      <div className="h-3 bg-neutral-700 rounded" />
                      <div className="h-3 bg-neutral-700 rounded" />
                      <div className="h-3 bg-neutral-700 rounded" />
                    </div>
                  </div>
                </div>
              ))
            : table.getRowModel().rows.map((row) => (
                <div
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={cn(
                    'bg-neutral-800/30 rounded-lg p-4 border border-neutral-700/50',
                    onRowClick && 'active:bg-neutral-800/50 transition-colors',
                    selectedRowId &&
                      getRowId &&
                      getRowId(row.original) === selectedRowId
                      ? 'border-teal-500/50 bg-teal-900/10'
                      : '',
                  )}
                >
                  {renderMobileItem(row.original)}
                </div>
              ))}
        </div>
      )}

      <div
        className={cn('overflow-x-auto', renderMobileItem && 'hidden md:block')}
      >
        <table
          className={cn('w-full', tableClassName)}
          style={{ tableLayout: 'fixed' }}
        >
          <thead className={theadClassName}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className={cn(
                  'text-left border-b border-neutral-700/70',
                  headerRowClassName,
                )}
              >
                {headerGroup.headers.map((header) => {
                  if (header.isPlaceholder) return null;
                  const meta = header.column.columnDef.meta as
                    | ColumnMeta
                    | undefined;
                  const canSort = header.column.getCanSort();
                  const sortDirection = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className={cn(
                        'px-6 py-3 text-sm font-medium text-neutral-300',
                        canSort &&
                          'cursor-pointer select-none hover:text-neutral-200 transition-colors duration-150',
                        meta?.headerClassName,
                        meta?.cellClassName,
                      )}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex items-center gap-2 w-full text-left"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          <span className="text-xs">
                            {sortDirection === 'asc' && '↑'}
                            {sortDirection === 'desc' && '↓'}
                            {!sortDirection && '↕'}
                          </span>
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className={tbodyClassName}>
            {isInitialLoading
              ? Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                  <tr
                    key={`skeleton-row-${rowIndex}`}
                    style={{ height: '56px' }}
                    className="border-b border-neutral-800"
                    aria-hidden="true"
                  >
                    {visibleColumns.map((column, colIndex) => {
                      const meta = column.columnDef.meta as
                        | ColumnMeta
                        | undefined;
                      return (
                        <td
                          key={`skeleton-cell-${rowIndex}-${column.id}`}
                          style={{ width: column.getSize(), height: '56px' }}
                          className={cn(
                            'px-6 py-2 align-middle',
                            meta?.cellClassName,
                          )}
                          aria-label="Loading"
                        >
                          <div
                            className="h-3 rounded bg-neutral-700/70 animate-pulse"
                            style={{
                              width:
                                colIndex === 0
                                  ? '70%'
                                  : colIndex % 3 === 0
                                    ? '50%'
                                    : '60%',
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))
              : table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    style={{ height: '56px' }}
                    className={getRowClass(row.original, row.index)}
                    onClick={() => onRowClick?.(row.original)}
                    data-ticket-id={getRowId?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta as
                        | ColumnMeta
                        | undefined;
                      return (
                        <td
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            height: '56px',
                          }}
                          className={cn(
                            'px-6 py-2 align-middle',
                            meta?.cellClassName,
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
